import { Helmet } from 'react-helmet-async';

interface SEOProps {
  title: string;
  description: string;
  keywords?: string;
  ogImage?: string;
  canonical?: string;
  noindex?: boolean;
  type?: 'website' | 'article' | 'product';
  publishedTime?: string;
  modifiedTime?: string;
  author?: string;
  section?: string;
  tags?: string[];
}

export const SEO = ({ 
  title, 
  description, 
  keywords = "ugc video ajansı, ugc içerik üretimi, e-ticaret video reklamları, ugc türkiye, instagram reels video, tiktok reklamları, ürün tanıtım videosu, ai video oluşturucu türkçe, yapay zeka video, sosyal medya videoları",
  ogImage = "https://ugcvideoolusturucu.com.tr/logo.png",
  canonical,
  noindex = false,
  type = 'website',
  publishedTime,
  modifiedTime,
  author = "UGC Video Oluşturucu",
  section,
  tags = []
}: SEOProps) => {
  const siteUrl = "https://ugcvideoolusturucu.com.tr";
  const siteName = "UGC Video Oluşturucu | Türkiye'nin #1 UGC Video Ajansı";
  const twitterHandle = "@ugcvideo_tr";
  
  // Google 2025: Title 50-60 chars for better CTR
  const fullTitle = title.length <= 60 ? title : title.substring(0, 57) + '...';
  // Description max 155-160 chars
  const metaDescription = description.length <= 160 ? description : description.substring(0, 157) + '...';
  const canonicalUrl = canonical || `${siteUrl}${typeof window !== 'undefined' ? window.location.pathname : ''}`;
  
  // Enhanced OG Image with fallback
  const ogImageUrl = ogImage.startsWith('http') ? ogImage : `${siteUrl}${ogImage}`;
  
  return (
    <Helmet>
      {/* Primary Meta Tags */}
      <title>{fullTitle}</title>
      <meta name="title" content={fullTitle} />
      <meta name="description" content={metaDescription} />
      <meta name="keywords" content={keywords} />
      <meta name="author" content={author} />
      
      {/* Robots - Google 2025 Extended */}
      <meta name="robots" content={noindex ? "noindex, nofollow" : "index, follow, max-image-preview:large, max-snippet:-1, max-video-preview:-1"} />
      <meta name="googlebot" content={noindex ? "noindex, nofollow" : "index, follow, max-snippet:-1, max-image-preview:large, max-video-preview:-1"} />
      <meta name="bingbot" content={noindex ? "noindex, nofollow" : "index, follow"} />
      
      {/* Canonical & Language */}
      <link rel="canonical" href={canonicalUrl} />
      <link rel="alternate" hrefLang="tr" href={canonicalUrl} />
      <link rel="alternate" hrefLang="x-default" href={canonicalUrl} />
      
      {/* Open Graph / Facebook - Enhanced for 2025 */}
      <meta property="og:type" content={type} />
      <meta property="og:url" content={canonicalUrl} />
      <meta property="og:title" content={fullTitle} />
      <meta property="og:description" content={metaDescription} />
      <meta property="og:image" content={ogImageUrl} />
      <meta property="og:image:secure_url" content={ogImageUrl} />
      <meta property="og:image:type" content="image/png" />
      <meta property="og:image:width" content="1200" />
      <meta property="og:image:height" content="630" />
      <meta property="og:image:alt" content={fullTitle} />
      <meta property="og:site_name" content={siteName} />
      <meta property="og:locale" content="tr_TR" />
      <meta property="og:locale:alternate" content="en_US" />
      
      {/* Article specific - Enhanced */}
      {type === 'article' && publishedTime && (
        <meta property="article:published_time" content={publishedTime} />
      )}
      {type === 'article' && modifiedTime && (
        <meta property="article:modified_time" content={modifiedTime} />
      )}
      {type === 'article' && (
        <>
          <meta property="article:author" content={author} />
          <meta property="article:publisher" content={siteUrl} />
          {section && <meta property="article:section" content={section} />}
          {tags.map((tag, index) => (
            <meta key={index} property="article:tag" content={tag} />
          ))}
        </>
      )}
      
      {/* Twitter Card - Enhanced for 2025 */}
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:site" content={twitterHandle} />
      <meta name="twitter:creator" content={twitterHandle} />
      <meta name="twitter:url" content={canonicalUrl} />
      <meta name="twitter:title" content={fullTitle} />
      <meta name="twitter:description" content={metaDescription} />
      <meta name="twitter:image" content={ogImageUrl} />
      <meta name="twitter:image:alt" content={fullTitle} />
      <meta name="twitter:domain" content="ugcvideoolusturucu.com.tr" />
      
      {/* Mobile Optimization - Google 2025 */}
      <meta name="viewport" content="width=device-width, initial-scale=1.0, maximum-scale=5.0" />
      <meta name="theme-color" content="#E31B54" />
      <meta name="mobile-web-app-capable" content="yes" />
      <meta name="apple-mobile-web-app-capable" content="yes" />
      <meta name="apple-mobile-web-app-status-bar-style" content="black-translucent" />
      <meta name="apple-mobile-web-app-title" content="UGC Video" />
      
      {/* Additional SEO Signals */}
      <meta name="language" content="Turkish" />
      <meta name="geo.region" content="TR" />
      <meta name="geo.placename" content="Turkey" />
      <meta name="geo.position" content="39.925533;32.866287" />
      <meta name="ICBM" content="39.925533, 32.866287" />
      <meta httpEquiv="content-language" content="tr" />
      <meta httpEquiv="X-UA-Compatible" content="IE=edge" />
      
      {/* Content freshness signal */}
      {modifiedTime && <meta name="revised" content={modifiedTime} />}
      
      {/* Schema.org hints */}
      <meta itemProp="name" content={fullTitle} />
      <meta itemProp="description" content={metaDescription} />
      <meta itemProp="image" content={ogImageUrl} />
      
      {/* Pinterest */}
      <meta name="pinterest-rich-pin" content="true" />
      
      {/* Google Site Verification - Placeholder for user to add */}
      {/* <meta name="google-site-verification" content="YOUR_VERIFICATION_CODE" /> */}
    </Helmet>
  );
};
